import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchPriorityList, } from '@/entities/agent';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, HelpTip, IconButton, MetaStrip, Skeleton, StageTabs, } from '@/shared/ui';
import { PriorityFilters, } from './priority-filters';
import { PriorityTable } from './priority-table';
const GROUP_TABS = [
    { id: 'all', label: 'Все' },
    { id: '1', label: '1 · 100' },
    { id: '2', label: '2 · 90' },
    { id: '3', label: '3 · 80' },
    { id: '4', label: '4 · 70' },
    { id: '5', label: '5 · 60' },
];
function parseGroup(value) {
    if (value === '1' || value === '2' || value === '3' || value === '4' || value === '5') {
        return value;
    }
    return 'all';
}
function parseStatus(value) {
    if (value === 'matched' || value === 'review')
        return value;
    return 'all';
}
function matchesStatus(item, status) {
    if (status === 'matched')
        return item.status === 'matched';
    if (status === 'review')
        return item.status === 'needs_review';
    return true;
}
export function PriorityPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const group = parseGroup(searchParams.get('group'));
    const filters = useMemo(() => ({
        q: searchParams.get('q') ?? '',
        status: parseStatus(searchParams.get('status')),
    }), [searchParams]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await fetchPriorityList({
                q: filters.q || undefined,
            }));
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.actionFailed);
            setData(null);
        }
        finally {
            setLoading(false);
        }
    }, [filters.q]);
    useEffect(() => {
        void load();
    }, [load]);
    function writeParams(next) {
        const params = new URLSearchParams(searchParams);
        if ('q' in next) {
            if (!next.q)
                params.delete('q');
            else
                params.set('q', next.q);
        }
        if ('group' in next) {
            if (!next.group || next.group === 'all')
                params.delete('group');
            else
                params.set('group', next.group);
        }
        if ('status' in next) {
            if (!next.status || next.status === 'all')
                params.delete('status');
            else
                params.set('status', next.status);
        }
        setSearchParams(params, { replace: true });
    }
    function applyFilters(next) {
        writeParams({ q: next.q, status: next.status });
    }
    function resetFilters() {
        writeParams({ q: '', status: 'all' });
    }
    function applyGroup(next) {
        writeParams({ group: next });
    }
    function openEntry(id) {
        navigate(`/priority/${encodeURIComponent(id)}`);
    }
    const byStatus = useMemo(() => {
        if (!data)
            return [];
        return data.items.filter((item) => matchesStatus(item, filters.status));
    }, [data, filters.status]);
    const rows = useMemo(() => {
        if (group === 'all')
            return byStatus;
        const groupNumber = Number(group);
        return byStatus.filter((item) => item.group === groupNumber);
    }, [byStatus, group]);
    const stageTabItems = useMemo(() => GROUP_TABS.map((tab) => {
        if (tab.id === 'all') {
            return { id: tab.id, label: tab.label, count: byStatus.length };
        }
        const groupNumber = Number(tab.id);
        return {
            id: tab.id,
            label: tab.label,
            count: byStatus.filter((item) => item.group === groupNumber).length,
        };
    }), [byStatus]);
    const catalogTotal = data?.items.length ?? 0;
    const pendingInSlice = rows.filter((item) => item.status === 'needs_review').length;
    return (_jsxs("div", { className: "space-y-3 px-0 py-1", children: [_jsxs("header", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsx("div", { className: "space-y-1", children: _jsx("h1", { className: "text-xl font-semibold", children: "\u041F\u0440\u0438\u043E\u0440\u0438\u0442\u0435\u0442\u043D\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A" }) }), _jsx(HelpTip, { open: helpOpen, onOpenChange: setHelpOpen, align: "end", title: "\u041F\u0440\u0438\u043E\u0440\u0438\u0442\u0435\u0442\u043D\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A", trigger: _jsx(IconButton, { size: "sm", pressed: helpOpen, "aria-label": "\u0421\u043F\u0440\u0430\u0432\u043A\u0430 \u043F\u043E \u043F\u0440\u0438\u043E\u0440\u0438\u0442\u0435\u0442\u043D\u043E\u043C\u0443 \u0441\u043F\u0438\u0441\u043A\u0443", "aria-expanded": helpOpen, children: _jsx(Info, { "aria-hidden": true }) }), children: _jsx("p", { children: "\u0413\u0440\u0443\u043F\u043F\u044B 1\u20135 \u0437\u0430\u0434\u0430\u044E\u0442 \u0432\u043D\u0435\u0448\u043D\u0438\u0439 \u0440\u0435\u0439\u0442\u0438\u043D\u0433 100\u202660. \u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435 \u0441\u0432\u044F\u0437\u0438 \u043D\u0435 \u0441\u043E\u0437\u0434\u0430\u0451\u0442 \u0446\u0438\u043A\u043B. \u041C\u0435\u043D\u044F\u044E\u0442\u0441\u044F \u0442\u043E\u043B\u044C\u043A\u043E \u0441\u0432\u044F\u0437\u044C \u0438 \u0432\u043D\u0435\u0448\u043D\u0438\u0439 \u0440\u0435\u0439\u0442\u0438\u043D\u0433." }) })] }), _jsx(StageTabs, { items: stageTabItems, value: group, onValueChange: applyGroup, "aria-label": "\u0413\u0440\u0443\u043F\u043F\u044B \u043F\u0440\u0438\u043E\u0440\u0438\u0442\u0435\u0442\u043D\u043E\u0433\u043E \u0441\u043F\u0438\u0441\u043A\u0430" }), _jsx(PriorityFilters, { filters: filters, total: rows.length, onChange: applyFilters, onReset: resetFilters }), _jsx(MetaStrip, { primary: `${rows.length} в срезе`, items: [
                    _jsxs(_Fragment, { children: ["\u0438\u0437 ", catalogTotal] }),
                    _jsxs(_Fragment, { children: ["\u041D\u0430 \u0441\u0432\u0435\u0440\u043A\u0443: ", pendingInSlice] }),
                ] }), error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(), children: uiCopy.retry })] })) : null, loading ? (_jsx(Skeleton, { className: "h-64 w-full" })) : data ? (_jsx(PriorityTable, { rows: rows, onOpenEntry: openEntry })) : null] }));
}
